import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '../ui/card';
import { Button } from '../../components/ui/button';
import { formatCurrency } from '../../lib/format';
import type { ComponentProps } from 'react';

export interface DashboardCardProps extends ComponentProps<typeof Card> {
  title: string;
  description?: string;
  count?: number;
  icon?: React.ReactNode;
  iconLabel?: string;
  onIconClick?: () => void;
  value?: string | number;
  trend?: {
    value: number;
    label: string;
  };
  collapsible?: boolean;
}

export function DashboardCard({
  title,
  description,
  count,
  icon,
  iconLabel,
  onIconClick,
  value,
  trend,
  children,
  collapsible,
  ...props
}: DashboardCardProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <Card {...props}>
      <CardHeader>
        <div className="flex items-start justify-between gap-1">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <CardTitle className="text-sm font-medium text-foreground-subtle">
                {title}
              </CardTitle>
              {count !== undefined && (
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                  {count}
                </span>
              )}
            </div>
            {description && <CardDescription>{description}</CardDescription>}
          </div>
          <div className="flex items-center gap-1">
            {icon && onIconClick ? (
              <Button
                variant="outline"
                onClick={onIconClick}
                size="sm"
                className="flex cursor-pointer text-primary items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-colors hover:border-border hover:bg-muted hover:text-primary [&_svg]:size-4"
              >
                {icon}
                {iconLabel && <span>{iconLabel}</span>}
              </Button>
            ) : icon ? (
              <div className="text-muted-foreground [&_svg]:size-5">{icon}</div>
            ) : null}
            {collapsible && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsExpanded((prev) => !prev)}
                className="text-muted-foreground hover:text-foreground p-1"
              >
                {isExpanded ? (
                  <ChevronUp className="size-4" />
                ) : (
                  <ChevronDown className="size-4" />
                )}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      {isExpanded && (
        <CardContent>
          {value !== undefined && (
            <div className="flex flex-col gap-1">
              <div className="text-2xl font-bold text-foreground">
                {typeof value === 'number'
                  ? `R$ ${formatCurrency(value)}`
                  : value}
              </div>
              {trend && (
                <div className="flex items-center gap-1 text-xs">
                  <span
                    className={
                      trend.value >= 0 ? 'text-success' : 'text-destructive'
                    }
                  >
                    {trend.value >= 0 ? '+' : ''}
                    {trend.value}%
                  </span>
                  <span className="text-muted-foreground">{trend.label}</span>
                </div>
              )}
            </div>
          )}
          {children}
        </CardContent>
      )}
    </Card>
  );
}
